import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { usePractice } from '../hooks/usePractice'
import { supabase, getSubscription, hasAccess, trialDaysRemaining, createCheckoutSession } from '../lib/supabase'
import Logo from './Logo'

export default function SubscriptionGate({ children }) {
  const { user, loading: authLoading } = useAuth()
  const { practiceId, loading: practiceLoading } = usePractice()
  const [sub, setSub]               = useState(null)
  const [subLoaded, setSubLoaded]   = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(null)

  useEffect(() => {
    if (authLoading || practiceLoading) return

    if (!user) {
      // For the no-user case we never call hasAccess(sub). The render
      // at line 53 returns children directly. So we don't need to mark
      // subLoaded as true here.
      //
      // Critical: don't set subLoaded=true for this case. If we did,
      // a later sign-in (user goes from null to a real user) would
      // re-fire this effect to fetch, but subLoaded would stay true
      // from the no-user pass, and the render would fall through to
      // hasAccess(sub=null) → false → lock screen, briefly, until the
      // fetch resolves. Leaving subLoaded false until a real fetch
      // resolves means the blank loading screen shows during that
      // gap instead of the lock screen flash.
      return
    }

    // Always fetch subscription from the practice owner's row (practiceId)
    // Team members don't have their own subscription row
    //
    // Note: we intentionally do NOT setSubLoaded(false) here. If this
    // effect re-fires (e.g. practiceId changed, or some other valid
    // trigger), the previous sub data is still accurate enough to render
    // against for the ~500ms the refetch takes. Resetting subLoaded would
    // blank the whole app (line 63 returns a placeholder div for
    // !subLoaded), which is the worst possible UX -- bookkeepers report
    // a white flash every time they tab back to the app. Letting the
    // existing data stand while the fresh fetch completes gives a smooth
    // no-flicker update on success.
    const idToFetch = practiceId || user.id
    getSubscription(idToFetch).then(({ data }) => {
      setSub(data)
      setSubLoaded(true)
    })
  }, [user, authLoading, practiceId, practiceLoading])

  const handleSubscribe = async (tier) => {
    setCheckoutLoading(tier)
    await createCheckoutSession(tier)
    setCheckoutLoading(null)
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    window.location.href = '/login'
  }

  // ── Still loading auth, practice, or subscription ──────────────────────────
  // Show a blank screen so the lock screen doesn't flash before we know
  // the real state. For logged-out users we still need to render quickly
  // so login / public pages appear; only block while there's a user
  // whose access state we haven't confirmed yet.
  if (authLoading) {
    return <div style={{ minHeight: '100vh', background: 'var(--surface)' }} />
  }
  if (user && (practiceLoading || !subLoaded)) {
    return <div style={{ minHeight: '100vh', background: 'var(--surface)' }} />
  }

  // ── No user — let the router handle it (login/signup pages) ─────────────────
  if (!user) return children

  // ── User has access (active subscription or valid trial) ─────────────────────
  if (hasAccess(sub)) {
    const daysLeft   = trialDaysRemaining(sub)
    const showBanner = sub?.subscription_status === 'trialing' && daysLeft <= 7

    return (
      <>
        {showBanner && (
          <div style={{
            background: daysLeft <= 3 ? '#dc2626' : 'var(--amber)',
            color: 'white', textAlign: 'center',
            padding: '10px 24px', fontSize: '0.85rem', fontWeight: 500,
          }}>
            {daysLeft === 0
              ? 'Your free trial expires today — '
              : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left in your free trial — `}
            <Link to="/pricing/account" style={{ color: 'white', fontWeight: 700, textDecoration: 'underline' }}>
              Choose a plan to continue
            </Link>
          </div>
        )}
        {children}
      </>
    )
  }

  // ── Past due — warning banner but keep access ────────────────────────────────
  if (sub?.subscription_status === 'past_due') {
    return (
      <>
        <div style={{
          background: '#dc2626', color: 'white', textAlign: 'center',
          padding: '10px 24px', fontSize: '0.85rem', fontWeight: 500,
        }}>
          ⚠ Your payment failed — please{' '}
          <Link to="/pricing/account" style={{ color: 'white', fontWeight: 700, textDecoration: 'underline' }}>
            update your payment details
          </Link>
          {' '}to avoid losing access.
        </div>
        {children}
      </>
    )
  }

  // ── Subscription ended or trial expired — lock screen ───────────────────────
  // Only lock out if truly expired — canceled accounts within their paid period
  // are handled by hasAccess above and never reach here.
  const isExpiredTrial = sub?.subscription_status === 'trialing' && trialDaysRemaining(sub) === 0

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 540, width: '100%', textAlign: 'center' }}>

        <Logo variant="icon" size={56} style={{ margin: '0 auto 24px' }} />

        <h2 style={{ marginBottom: 12 }}>
          {isExpiredTrial ? 'Your free trial has ended' : 'Your subscription has ended'}
        </h2>
        <p style={{ color: 'var(--ink-muted)', marginBottom: 36, lineHeight: 1.6, fontSize: '0.95rem' }}>
          {isExpiredTrial
            ? 'You\'ve reached the end of your 30-day free trial. Choose a plan to continue using Receipt Chaser.'
            : 'Your subscription has been cancelled. Resubscribe to regain access.'}
        </p>

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 28 }}>
          {[
            { tier: 'per_client', label: 'Per client', price: '£3/client/month', desc: 'Pay only for what you use' },
            { tier: 'pro',        label: 'Pro',        price: '£25/month',       desc: 'Unlimited clients' },
          ].map(plan => (
            <div key={plan.tier} style={{
              flex: 1, minWidth: 200,
              border: `2px solid ${plan.tier === 'pro' ? 'var(--accent)' : 'var(--surface-3)'}`,
              borderRadius: 'var(--radius-lg)', padding: '20px 24px',
              background: 'var(--white)',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{plan.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', marginBottom: 4 }}>{plan.price}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', marginBottom: 16 }}>{plan.desc}</div>
              <button
                className={`btn ${plan.tier === 'pro' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => handleSubscribe(plan.tier)}
                disabled={checkoutLoading !== null}
              >
                {checkoutLoading === plan.tier ? 'Redirecting…' : 'Subscribe'}
              </button>
            </div>
          ))}
        </div>

        <button
          className="btn btn-ghost btn-sm"
          onClick={handleSignOut}
          style={{ color: 'var(--ink-faint)' }}
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
